import type { MouseEventHandler } from 'react'
import { useCallback } from 'react'
import useSetImmersiveMode from './useImmersiveModeHandlers'
import BookHotelFlights from '../components/TripModal/RestoreCart/BookHotelFlight'
import { getRestoreCartUriForId } from 'checkout/lib/utils/misc/restoreCartUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useNavigateTo from 'hooks/useNavigateTo'
import noop from 'lib/function/noop'
import { getCurrentUserId } from 'selectors/accountSelectors'
import BookTourFlight from 'tripPlanner/components/TripModal/RestoreCart/BookTourFlight'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { LETripItem } from 'tripPlanner/types/tripItem'
import {
  isBookableHotelFlightBundle,
  isBookableTourV1FlightBundle,
} from 'tripPlanner/utils/bookmark/restoreCart'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { getCartItems } from 'api/cart/getCartItems'

type ReturnValue = {
  onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
  canRestoreCart: boolean
}

export default function useRestoreCart(
  item: LETripItem,
  fallbackUrl: string,
  {
    hotel,
  }: {
    hotel?:
      | App.HotelOffer
      | App.BedbankOffer
      | App.BundleOffer
      | App.VillaOffer
      | undefined
  } = {},
): ReturnValue {
  const currentUserId = useAppSelector(getCurrentUserId)

  const canEdit = useCanEditTrip()

  const { setImmersiveMode } = useSetImmersiveMode()
  const openModal = useOpenModal()

  const navigateTo = useNavigateTo()

  const modalElement = useModalElementContext()
  const onClick = useCallback(
    async(e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const canBookOfferStandalone =
        hotel && !(hotel as App.Offer).bundledWithFlightsOnly

      if (item.savedItemData.cartId) {
        let canRestoreCart = false
        await getCartItems(item.savedItemData.cartId)
          .then(() => {
            canRestoreCart = true
          })
          .catch(noop)

        if (canRestoreCart) {
          const restoreCartUrl = getRestoreCartUriForId(
            item.savedItemData.cartId,
          )

          if (
            item.sourceType !== 'LE_Accommodation_Rental' &&
            isBookableHotelFlightBundle(item) &&
            canBookOfferStandalone
          ) {
            openModal(BookHotelFlights, { item, restoreCartUrl })
          } else if (
            isBookableTourV1FlightBundle(item) &&
            canBookOfferStandalone
          ) {
            openModal(BookTourFlight, { item, restoreCartUrl })
          } else {
            navigateTo(restoreCartUrl)
          }
          return
        }
      }

      if (canEdit) {
        setImmersiveMode({
          tripId: item.tripId,
          sectionId: tripItemSelectionId(item.id),
        })
      }

      navigateTo(fallbackUrl)
      modalElement?.resolve()
    },
    [
      canEdit,
      item,
      modalElement,
      navigateTo,
      hotel,
      openModal,
      fallbackUrl,
      setImmersiveMode,
    ],
  )

  const canRestoreCart =
    !!item.savedItemData.cartId && currentUserId === item.createdBy

  return {
    canRestoreCart,
    onClick,
  }
}
